import React from 'react';

/**
 * Full-screen branded overlay used by the admin "Sign in as user" flow.
 *
 * Two visual variants:
 *   - 'loading' (default): spinner + shimmer skeleton bars while the session
 *     swap is in flight.
 *   - 'error': calm warning glyph + actionable copy + a primary "Got it"
 *     button. Used for expired / invalid / network failures so the admin
 *     gets a clear, non-scary explanation instead of an auto-dismissed flash.
 *
 * The component is presentational; the parent (App.js) is responsible for
 * keeping it visible until the user dismisses an error.
 * Styles live in src/styles/loaders.css.
 */
function ImpersonationLoader({
  variant = 'loading',
  status = 'Signing in…',
  subtext = '',
  actionLabel = 'Got it',
  onAction,
}) {
  const isError = variant === 'error';
  return (
    <div
      className="impersonation-loader-overlay"
      role={isError ? 'alertdialog' : 'status'}
      aria-live={isError ? 'assertive' : 'polite'}
      aria-modal={isError ? 'true' : undefined}
    >
      <div className={`impersonation-loader-card${isError ? ' error' : ''}`}>
        <img
          className="impersonation-loader-logo"
          src={process.env.PUBLIC_URL + '/assets/img/KJUUP-Schrift-Icon.png'}
          alt="KJUUP"
        />

        {isError ? (
          <div className="impersonation-loader-badge" aria-hidden="true">
            <div className="impersonation-loader-badge-inner">!</div>
          </div>
        ) : (
          <div className="impersonation-loader-spinner-wrap" aria-hidden="true">
            <div className="impersonation-loader-spinner-glow" />
            <div className="impersonation-loader-spinner-ring" />
          </div>
        )}

        <div>
          <div className="impersonation-loader-status">{status}</div>
          {subtext ? (
            <div className="impersonation-loader-subtext">{subtext}</div>
          ) : null}
        </div>

        {isError ? (
          <button
            type="button"
            className="kj-btn kj-btn--primary kj-btn--lg impersonation-loader-action"
            onClick={onAction}
            autoFocus
          >
            {actionLabel}
          </button>
        ) : (
          <div className="impersonation-loader-skeleton-row" aria-hidden="true">
            <div className="impersonation-loader-skeleton-bar" />
            <div className="impersonation-loader-skeleton-bar short" />
          </div>
        )}
      </div>
    </div>
  );
}

export default ImpersonationLoader;
